/**
 * Kurssicherungen (das DTG-Register, `DB.fxDeals[]`).
 *
 * ===================== GEMESSEN STATT ABGELEITET (Fix-Runde 1 zu Task 5a) ========================
 *
 * Bis zu dieser Runde stand hier "ANNAHME: Pfad `/api/fxDeals`, aus dem SSE-Entitaetsnamen
 * abgeleitet" - mit dem ausdruecklichen Zusatz, entschiede Phase 4a sich fuer `/api/fx-deals`,
 * muesse der Entitaetsname mitgezogen werden. Phase 4a hat sich fuer `/api/fx-deals` entschieden,
 * und nichts wurde mitgezogen:
 *
 *   Ereignisname `"fxDeal"` (Einzahl) -> `backend/src/http/routes/fxDeals.ts:119,173,221,261,287`
 *   REST-Pfad    `/api/fx-deals`      -> `backend/src/http/endpunktMatrix.ts:107-110`
 *
 * `/api/fxDeals` gibt es nicht. Das war nicht latent: `kurssicherungenLaden()` laeuft beim Start der
 * SPA (`state/Speicher.tsx#bestandLaden`) - Kurssicherungen wurden also NIE geladen.
 *
 * ===================== WAS ES UNTER /api/fx-deals WIRKLICH GIBT ==================================
 *
 * `backend/src/http/endpunktMatrix.ts:107-113`, mit `backend/src/http/routes/fxDeals.ts` Zeile fuer
 * Zeile abgeglichen:
 *
 *   GET    /api/fx-deals                                  -> Liste (mit `gebunden` und `frei`)
 *   POST   /api/fx-deals                                  -> flache Felder, "no" wird ignoriert (systemvergebene Nummer)
 *   PATCH  /api/fx-deals/:dealId                          -> `{ rowVersion, felder }`
 *   DELETE /api/fx-deals/:dealId                          -> `{ rowVersion }`
 *   POST   /api/fx-deals/:dealId/allocations              -> Zuordnung zu einer Bestellung
 *   DELETE /api/fx-deals/:dealId/allocations/:allocationId
 *
 * ES GIBT KEIN `GET /api/fx-deals/:dealId` - der Router kennt nur `router.get("/")`. Genau diese
 * Route brauchte aber der Nachlader dieser Entitaet. `zugriff.einzeln` haette 404 bekommen,
 * `anfrageOderNull` daraus `null` gemacht, und der Reduzierer haette den Datensatz auf ein
 * FREMDES Ereignis hin AUS DER LISTE GEWORFEN (`reduzierer.ts#listeErsetzen`) - aus einer toten
 * Aktualisierung waere eine schaedliche geworden. `kurssicherungLaden` unten faellt deshalb auf die
 * Liste zurueck; die Begruendung steht dort.
 *
 * `kurssicherungAendern` ist WEITERHIN NICHT gebaut: `zugriff.aendern` sendet `PUT /api/fx-deals/:id`
 * mit flachem Koerper - die Route ist ein PATCH und die Koerperform `{ rowVersion, felder }`
 * (`fxDeals.ts:127-160`). Eine Funktion, die nachweislich die falsche Methode auf die falsche
 * Koerperform schickt, ist eine Falle fuer den naechsten Aufrufer; die Drahtform steht oben, wer sie
 * baut, baut sie gegen das Backend.
 *
 * `kurssicherungAnlegen` und `kurssicherungLoeschen` dagegen sind seit Task 8 (Verdrahtungsplan
 * Phase 6, "Zahlungen & Kurssicherung") WIEDER da: `zugriff.anlegen` (POST, flacher Koerper) und
 * `zugriff.loeschen` (DELETE, Koerper `{ rowVersion }`) treffen die Route tatsaechlich - anders als
 * `.aendern` oben ist hier NICHTS falsch an der Form, die `ressource()` erwartet. Siehe die
 * Kopfkommentare der beiden Funktionen unten.
 */
import { anfrage, ladeHoch, ressource } from "./basis.ts";
import type { FxDeal } from "../lib/fx.ts";

export interface FxAccount {
  id: string;
  name: string;
  currency: "USD";
  isActive: boolean;
  isDefault: boolean;
  rowVersion: number;
  /** Anlagezeit (ISO); nur Rueckfall zur Unterscheidung gleichnamiger aktiver Konten. */
  createdAt?: string;
}

export interface FxBalanceSnapshot {
  id: string;
  accountId: string;
  capturedOn: string;
  previousBalanceUsd: string;
  previousMixedRate: string;
  balanceUsd: string;
  mixedRate: string;
  dealId: string;
  createdAt?: string;
  dealUsd: string;
  dealRate: string;
}

export interface FxRatePoint {
  id: string;
  rateDate: string;
  eurUsdRate: string;
  source: "ECB";
  fetchedAt: string;
}

export interface FxRateStatus {
  state: "fresh" | "stale";
  latestRate: FxRatePoint | null;
  latestImportRun: {
    startedAt: string;
    finishedAt: string | null;
    source: string;
    latestRateDate: string | null;
  } | null;
}

export interface FxOverview {
  accounts?: FxAccountOverview[];
  globalCurrent?: FxAccountCurrent;
  legacyUnassigned?: AccountHistoryItem[];
  /** Uebergangsvertrag, bis Task 5 die Bestandsoberflaeche auf accounts[] umstellt. */
  account: FxAccount;
  latestSnapshot: FxBalanceSnapshot | null;
  snapshots?: FxBalanceSnapshot[];
  deals: FxDeal[];
  /**
   * Weich gelöschte (nie übernommene) Sicherungen mit wirksamer `accountId` - nur für die Historie
   * (`deletedAt`, `deletedByName`, `deleteReason`). Sie stehen weder in `deals` noch in `openDeals`.
   */
  deletedDeals?: FxDeal[];
  openDeals?: FxDeal[];
  takenDeals?: FxDeal[];
  rates: FxRatePoint[];
  rateStatus: FxRateStatus;
}

/** Der GET-Vertrag enthaelt die konto-orientierte Projektion; Alt-Komponenten nutzen bis Task 5 noch den Uebergangsanteil. */
export interface FxOverviewResponse extends FxOverview {
  accounts: FxAccountOverview[];
  globalCurrent: FxAccountCurrent;
  legacyUnassigned: AccountHistoryItem[];
}

export interface FxAccountCurrent {
  balanceUsd: string;
  mixedRate: string | null;
}

export interface AccountHistoryItem {
  id: string;
  /** "deal": Sicherungs-Ereignis ohne Kontostand (heute nur `deal_deleted`, weich gelöscht). */
  origin: "event" | "legacy" | "deal";
  accountId: string | null;
  accountName: string | null;
  type: string;
  effectiveOn: string;
  recordedAt: string | null;
  actor: { id: string; label: string } | null;
  balanceUsd: string | null;
  mixedRate: string | null;
  /** `nummer` = systemvergebene Sicherungsnummer "S-<n>"; `no` das bisherige freie Feld (Zusatzinfo). */
  deal: { id: string; no: string; nummer?: string; usd: string; rate: string } | null;
  /** Bewegung = Stand danach - Stand davor desselben Kontos (vorzeichenbehaftet, USD); null beim Anfangsstand. Serverseitig berechnet. */
  deltaUsd?: string | null;
}

export interface FxAccountOverview {
  account: FxAccount;
  current: (FxAccountCurrent & { effectiveOn: string; recordedAt: string; eventId: string }) | null;
  history: AccountHistoryItem[];
}

/** Der Client behaelt USD-Cents als Text; der Server berechnet und validiert den Snapshot. */
export interface FxSnapshotInput {
  accountId: string;
  capturedOn: string;
  balanceUsd: string;
  dealId: string;
}

/** Ein manueller Stand ist ein eigenes Kontoereignis, niemals eine synthetische Sicherung. */
export interface FxManuellerKontostandInput {
  accountId: string;
  effectiveOn: string;
  balanceUsd: string;
  mixedRate: string;
}

/** Die vom Server mit PostgreSQL-numeric berechnete, nicht persistierende Annahmevorschau. */
export interface FxAnnahmeVorschau {
  accountId: string;
  dealId: string;
  capturedOn: string;
  previousBalanceUsd: string;
  previousMixedRate: string;
  balanceUsd: string;
  mixedRate: string;
}

// `hintergrund`: nur der automatische Nachlader (`useFxOverview`, wenn allein `fxDeals` wechselt) setzt
// das - dann geht die Anfrage als Hintergrundanfrage hinaus (`AnfrageOptionen.hintergrund`). Nur bei
// `true` mit Optionen aufgerufen: der Normalfall (Handlung des Menschen) bleibt der Aufruf ohne.
export const fxOverviewLaden = (hintergrund?: boolean) =>
  hintergrund
    ? anfrage<FxOverviewResponse>("/api/fx-overview", { hintergrund: true })
    : anfrage<FxOverviewResponse>("/api/fx-overview");

export const fxSnapshotAnlegen = (input: FxSnapshotInput) =>
  anfrage<FxBalanceSnapshot>("/api/fx-balance-snapshots", { methode: "POST", koerper: input });

export const fxManuellenKontostandAnlegen = (input: FxManuellerKontostandInput) =>
  anfrage<Record<string, unknown>>("/api/fx-account-events/manual-balance", { methode: "POST", koerper: input });

export const fxAnnahmeVorschauLaden = (
  accountId: string,
  input: Pick<FxSnapshotInput, "dealId" | "capturedOn">,
) => anfrage<FxAnnahmeVorschau>(
  `/api/fx-accounts/${encodeURIComponent(accountId)}/acceptance-preview`,
  { methode: "POST", koerper: input },
);

export const fxKontenLaden = () => anfrage<FxAccount[]>("/api/fx-accounts");

export interface FxKontoAnlegenDaten {
  name: string;
  currency: "USD";
}

export interface FxKontoAendernFelder {
  name?: string;
  isActive?: boolean;
}

export const fxKontoAnlegen = (daten: FxKontoAnlegenDaten) =>
  anfrage<FxAccount>("/api/fx-accounts", { methode: "POST", koerper: daten });

export const fxKontoAendern = (id: string, rowVersion: number, felder: FxKontoAendernFelder) =>
  anfrage<FxAccount>(`/api/fx-accounts/${encodeURIComponent(id)}`, {
    methode: "PATCH",
    koerper: { rowVersion, felder },
  });

/**
 * Name dieser Entitaet im SSE-Ereignis (`event.entity`) - EINZAHL. Gemessen an
 * `backend/src/http/routes/fxDeals.ts:119`; die Allowlist dazu ist `BROADCAST_ENTITIES` in
 * `backend/src/sse/broadcast.ts`. NICHT der Pfad.
 */
export const SSE_ENTITAET = "fxDeal";

/**
 * REST-Basispfad - mit BINDESTRICH. Gemessen an `backend/src/http/endpunktMatrix.ts:107`. Steht als
 * Literal und wird NICHT aus `SSE_ENTITAET` errechnet - genau diese Rechnung hat `/api/fxDeals`
 * erzeugt (Begruendung siehe `bestellungen.ts`).
 */
export const BASISPFAD = "/api/fx-deals";

const zugriff = ressource<FxDeal>(BASISPFAD);

/** `GET /api/fx-deals` - die einzige Leseroute dieser Entitaet. */
export const kurssicherungenLaden = zugriff.liste;

/**
 * Ein einzelner Deal - ueber die LISTE, weil es `GET /api/fx-deals/:dealId` nicht gibt (siehe
 * Kopfkommentar). Bewusst so und nicht als `zugriff.einzeln` mit einem 404-Rueckfall: ein 404
 * bedeutet im Store "der Datensatz existiert nicht mehr" und WIRFT die Zeile aus der Liste - bei
 * einer Route, die es nie gab, waere das ein Datenverlust auf ein voellig gesundes Ereignis hin.
 *
 * `null` heisst hier deshalb, was es heissen soll: der Deal steht nicht mehr in der Liste des
 * Servers, ist also tatsaechlich weg. Der Preis ist ein Listenabruf je Ereignis - messbar teurer als
 * ein Einzelabruf, aber die einzige Form, die das Backend heute hergibt. Kommt die Einzelroute,
 * aendert sich AUSSCHLIESSLICH diese Funktion.
 */
export async function kurssicherungLaden(
  id: string,
  signal?: AbortSignal,
  // Nur der SSE-Nachlader setzt das (`AnfrageOptionen.hintergrund`).
  hintergrund?: boolean,
): Promise<FxDeal | null> {
  const alle = await kurssicherungenLaden(signal, hintergrund);
  return alle.find((deal) => String((deal as { id?: unknown }).id) === id) ?? null;
}

/**
 * `POST /api/fx-deals` (Task 8, Verdrahtungsplan Phase 6, `App.tsx#kurssicherungSpeichern` - der
 * ERSTE Aufrufer seit `kurssicherungAnlegen` in der Fix-Runde 1 entfernt wurde, siehe Kopfkommentar).
 * `zugriff.anlegen` passt hier tatsaechlich (anders als `.aendern`, dort bleibt PUT falsch): der
 * Router liest `req.body` flach und baut `felder` selbst daraus (`dealFelderAusKoerper`,
 * `backend/src/http/routes/fxDeals.ts:47-56`) - `ressource().anlegen` sendet `daten` genau so, ohne
 * `rowVersion`-Huelle.
 *
 * `daten` traegt bewusst NUR die sechs schreibbaren Spalten (`FX_DEAL_FELDER` in
 * `backend/src/domaene/kurssicherung.ts`: `no, date, usd, bank, rate, docFileId`) - `no` sendet der Client
 * NICHT mehr: die Nummer "S-<n>" vergibt das System (der Server ignoriert ein mitgeschicktes `no`). Die
 * sieben abgeleiteten Werte des Bestands (`oldBalance, oldRate, newBalance, mixedRate, oldShare,
 * newShare, weighting`) haben dafuer
 * KEINE Spalte - gemessen, nicht angenommen (siehe `App.tsx#kurssicherungSpeichern`).
 */
export const kurssicherungAnlegen = zugriff.anlegen;

/**
 * `DELETE /api/fx-deals/:dealId` mit `{ rowVersion }` (`fxDeals.ts:190-227`) - anders als
 * `kurssicherungAendern` (PUT, falsch, siehe Kopfkommentar) deckt sich `zugriff.loeschen` hier
 * tatsaechlich mit der Route: DELETE, Koerper `{ rowVersion }`, kein Ressourcenrumpf noetig. Seit der
 * Konsolidierung (2026-10-01) ohne Aufrufer in der Oberflaeche: geloescht wird nur noch weich
 * (`kurssicherungWeichLoeschen`, Sicherungstabelle -> Dialog mit Grund).
 */
export const kurssicherungLoeschen = zugriff.loeschen;

/**
 * `PATCH /api/fx-deals/:dealId` mit `{ rowVersion, felder }` (`fxDeals.ts:127-160`) - GEBAUT am
 * 2026-08-15, nachdem der Kopfkommentar oben sie jahrelang als „weiterhin nicht gebaut" gefuehrt
 * hat.
 *
 * Der dort genannte Grund bleibt richtig und ist genau der Grund fuer diese eigene Funktion:
 * `zugriff.aendern` sendet `PUT` mit FLACHEM Koerper, die Route ist ein `PATCH` mit der Huelle
 * `{ rowVersion, felder }`. Wer `ressource().aendern` benutzt haette, haette die falsche Methode auf
 * die falsche Form geschickt - deshalb steht hier die Drahtform ausgeschrieben, statt den Helfer zu
 * verbiegen.
 *
 * `felder` traegt SPALTENNAMEN in snake_case (`old_balance`), NICHT camelCase - und das ist der
 * Unterschied zu `POST /api/fx-deals` daneben, wo `dealFelderAusKoerper` einen flachen
 * camelCase-Koerper selbst umsetzt. Der PATCH-Zweig reicht `felder` unveraendert an
 * `pruefeFxDealFelder` weiter, und die prueft gegen `FX_DEAL_FELDER` - also gegen die Spaltennamen.
 * Ein camelCase-Schluessel liefe dort in `FeldNichtInGruppeError` (400).
 *
 * Deshalb setzt DIESE Funktion um, und zwar mit derselben mechanischen Regel wie das Backend
 * (`camelSchluessel`, nur rueckwaerts): ein Aufrufer soll nicht zwei Schreibweisen im Kopf behalten
 * muessen, je nachdem, ob er anlegt oder aendert.
 */
export async function kurssicherungAendern(
  id: string,
  felder: Record<string, unknown>,
  rowVersion: number,
): Promise<FxDeal> {
  const spalten = Object.fromEntries(
    Object.entries(felder).map(([feld, wert]) => [
      feld.replace(/[A-Z]/g, (zeichen) => `_${zeichen.toLowerCase()}`),
      wert,
    ]),
  );
  return await anfrage<FxDeal>(`${BASISPFAD}/${encodeURIComponent(id)}`, {
    methode: "PATCH",
    koerper: { rowVersion, felder: spalten },
  });
}

/**
 * Die drei Zuordnungswege (2026-08-15) - `bindFxToOpen` (index.html:9607), `patchFxAlloc` (:9600)
 * und `unbindFxFromOpen` (:9619).
 *
 * ===================== Warum EINZELN und nicht ueber `onDeals(ganzeListe)` =====================
 *
 * `Drawer/PaymentTab/ZahlungTab.tsx` und `PaymentsPage/SicherungDetail.tsx` (frueher `SicherungsListe.tsx`) bauen im Bestand eine
 * neue `deals`-Liste und reichen sie als GANZES weiter - das ist die Form des Bestands, der alles
 * im Speicher haelt. Ueber REST waere daraus ein Abgleich zweier Listen geworden ("welche
 * Zuordnung ist neu, welche fehlt jetzt, welcher Betrag hat sich geaendert"), und jeder Fehler
 * darin schriebe stillschweigend das Falsche. Die drei Funktionen hier bilden stattdessen genau
 * das ab, was das Backend kennt - eine Handlung, ein Aufruf.
 *
 * KEIN `rowVersion`: `fx_allocations` ist nicht versioniert (kein Eintrag in `TABELLEN_ALLOWLIST`,
 * `backend/src/versioning/rowVersion.ts`). Der Konflikt, der hier zaehlt, ist die DECKUNG des
 * Deals, und die prueft das Backend unter `FOR UPDATE` frisch - eine mitgeschickte Versionsnummer
 * saehe davon nichts.
 *
 * Alle drei liefern NICHTS zurueck, was der Store direkt uebernehmen koennte: die Antwort ist die
 * ZUORDNUNG, der Store fuehrt aber Deals. Der Aufrufer laedt deshalb den Deal nach - dieselbe
 * Nachlade-Form wie bei `drawerDokumentHochladen` (siehe `App.tsx`).
 */
export async function zuordnungAnlegen(
  dealId: string,
  orderId: string,
  usd: number,
): Promise<void> {
  await anfrage(`${BASISPFAD}/${encodeURIComponent(dealId)}/allocations`, {
    methode: "POST",
    koerper: { orderId, usd },
  });
}

/**
 * `PATCH /api/fx-deals/:dealId/allocations/:allocationId` mit `{ usd }` - die Route, die es bis zum
 * 2026-08-15 NICHT gab und ohne die weder „Betrag aendern" noch ein zweites „Binden" verdrahtbar
 * war (`POST` scheitert an `UNIQUE (deal_id, order_id)`).
 */
export async function zuordnungAendern(
  dealId: string,
  allocationId: string,
  usd: number,
): Promise<void> {
  await anfrage(
    `${BASISPFAD}/${encodeURIComponent(dealId)}/allocations/${encodeURIComponent(allocationId)}`,
    { methode: "PATCH", koerper: { usd } },
  );
}

/**
 * Der Beleg einer Kurssicherung (2026-08-15) - `POST`/`DELETE /api/fx-deals/:dealId/document`.
 *
 * NICHT der Dokumentweg der Bestellung: eine Sicherung fuehrt GENAU EINEN Beleg als
 * Fremdschluessel (`fx_deals.doc_file_id`), keine Checkliste mit 22 Typen und keine
 * Versionskette. Ein zweiter Upload ERSETZT deshalb, er legt nicht daneben.
 *
 * `FormData` und kein JSON: `ladeHoch()` laesst den Content-Type dem Browser (der setzt die
 * `boundary` selbst) - dieselbe Form wie `dokumentHochladen` in `api/dokumente.ts`. Der Feldname ist
 * beliebig, busboy liest den ersten Dateistrom. `fortschritt` meldet den Sendeanteil 0 bis 1.
 */
export async function belegHochladen(
  dealId: string,
  datei: File,
  fortschritt?: (anteil: number) => void,
): Promise<void> {
  const formular = new FormData();
  formular.append("datei", datei);
  await ladeHoch(`${BASISPFAD}/${encodeURIComponent(dealId)}/document`, formular, {
    onFortschritt: fortschritt,
  });
}

/**
 * `DELETE /api/fx-deals/:dealId/document` mit `{ rowVersion }`.
 *
 * VERSIONIERT, anders als das Hochladen: hier sieht der Nutzer den Beleg vor sich und entscheidet
 * gegen genau diesen Stand. Entfernt wird der VERWEIS - die Datei bleibt in der Ablage, es gibt in
 * diesem Baum keine Loeschroute fuer Dateien.
 */
export async function belegEntfernen(dealId: string, rowVersion: number): Promise<void> {
  await anfrage(`${BASISPFAD}/${encodeURIComponent(dealId)}/document`, {
    methode: "DELETE",
    koerper: { rowVersion },
  });
}

/** `DELETE /api/fx-deals/:dealId/allocations/:allocationId` (`fxDeals.ts:270`). */
export async function zuordnungLoeschen(dealId: string, allocationId: string): Promise<void> {
  await anfrage(
    `${BASISPFAD}/${encodeURIComponent(dealId)}/allocations/${encodeURIComponent(allocationId)}`,
    { methode: "DELETE" },
  );
}

/**
 * `DELETE /api/fx-deals/:dealId` mit `{ rowVersion, reason }` - seit Migration 050 ein WEICHES
 * Löschen: die Sicherung bleibt mit `deletedAt`/`deletedBy`/`deleteReason` stehen und erscheint in
 * der Konto-Historie als „gelöscht (nicht übernommen)“. Der Server lehnt mit 409 ab, wenn sie schon
 * übernommen, noch an Bestellungen gebunden oder bereits gelöscht ist; die Meldung steht in
 * `KonfliktFehler.message`. `reason` ist optional (leer = keiner).
 */
export async function kurssicherungWeichLoeschen(id: string, rowVersion: number, reason?: string): Promise<void> {
  const grund = reason?.trim();
  await anfrage(`${BASISPFAD}/${encodeURIComponent(id)}`, {
    methode: "DELETE",
    koerper: grund ? { rowVersion, reason: grund } : { rowVersion },
  });
}
